/**
 * WorkshopsCalendarHeader — the WorkshopsCalendar card's header row: title +
 * "X of Y" count, whatever filters the page passes in, the Month/Week date
 * navigator, and the Month/Week/List view toggle. Stateless.
 */
import { Plus } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { WorkshopsCalendarDateNavigator } from './WorkshopsCalendarDateNavigator';
import type { CalendarView } from './workshopsCalendarTypes';
import { WorkshopsCalendarViewToggle } from './WorkshopsCalendarViewToggle';

export interface WorkshopsCalendarHeaderProps {
  title: string;
  countLine: ReactNode;
  /** Filter controls drawn before the date navigator, e.g. a sub-region legend. */
  filters?: ReactNode;
  view: CalendarView;
  periodLabel: string;
  onNav: (dir: 'prev' | 'next') => void;
  views: CalendarView[];
  onSetView: (v: CalendarView) => void;
  /** When provided, a "New workshop" action shows in the header (BON-203).
   *  Omitted in read-only shells (e.g. teacher) so the action is absent. */
  onCreateWorkshop?: () => void;
}

export function WorkshopsCalendarHeader({
  title,
  countLine,
  filters,
  view,
  periodLabel,
  onNav,
  views,
  onSetView,
  onCreateWorkshop,
}: WorkshopsCalendarHeaderProps): ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '14px 18px',
        gap: 14,
        rowGap: 10,
        borderBottom: '1px solid var(--nb-border)',
      }}
    >
      <div>
        <h3
          className="nb-display"
          style={{ margin: 0, fontSize: 14, letterSpacing: '.04em', whiteSpace: 'nowrap' }}
        >
          {title}
        </h3>
        {countLine && (
          <div style={{ fontSize: 11, color: 'var(--nb-subtle)', marginTop: 2 }}>{countLine}</div>
        )}
      </div>
      <div style={{ flex: 1 }} />
      {onCreateWorkshop && (
        <button
          type="button"
          className="nb-btn nb-btn-primary"
          onClick={onCreateWorkshop}
          style={{ display: 'flex', alignItems: 'center', gap: 5 }}
        >
          <Plus size={13} /> New workshop
        </button>
      )}
      {filters}
      {view !== 'List' && <WorkshopsCalendarDateNavigator label={periodLabel} onNav={onNav} />}
      {views.length > 1 && (
        <WorkshopsCalendarViewToggle views={views} view={view} onSetView={onSetView} />
      )}
    </div>
  );
}
